import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { ShopFaq } from '../shared/ShopFaq';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { pageActions } from '../../data/pageActions';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { ConcernFinder } from '../home/ConcernFinder';
import { HomeProtocols } from '../home/HomeProtocols';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { SALON_CODE } from '../../data/codes';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mediaOr } from '../../core/rules/media';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { quietLink } from '../../ui/sections';
import { ClosingBand } from '../shared/ClosingBand';
import { plural } from '../../core/format/text';
import { RoutineCompare } from './RoutineCompare';
import { RoutineAnchor } from './RoutineAnchor';
import { anchorRoutine, ROUTINES_FAQ_COUNT } from './routineRules';

/**
 * Protocollen: de pagina met de hoogste orderwaarde. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wat is een protocol (hero, sand) · het complete protocol, met wat je
 * er cadeau bij krijgt (blush) · of begin bij je klacht: de klachtkiezer van Home, rustig, met
 * vergelijken in een uitklapper (shell) · wat er na je bestelling gebeurt: de tijdlijn van Home,
 * stil (sand) · abonnement (kort) · vragen · twijfel: de huidtest, met de code voor salonklanten
 * (blush; de footer is klei). Kopen is hier de hoofdactie: boeken staat in de kiezer als link.
 *
 * Klachtkiezer en tijdlijn zijn dezelfde onderdelen als op Home (één bron), hier zonder beweging:
 * Home is de speelse ontvangst, dit is een winkelpagina.
 */
export function Routines() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    routines,
    routineTotals
  } = useCatalog();
  const copy = pages.routines;
  const complete = anchorRoutine(routines, r => routineTotals(r).price);
  const heroMedia = mediaOr(copy.hero.media, {
    image: complete?.image || founder.withProducts,
    alt: complete ? `De producten van ${complete.name} op een rij` : `${founder.name} met de producten waar ze mee werkt`
  });
  return <>
      {/* Hero: zelfde opbouw als salon en over. In het beeld: wie het opstelt, en wat je meekrijgt. */}
      <PageHero titleId="routines-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8581" data-mp="2epnv6turutbn" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              Persoonlijk huidplan, opgesteld door <span className="text-ink" data-id="element-8582" data-mp="2tlnd0w7oo7tz">{founder.firstName}</span>
            </>,
      // "Vegan" alleen op de Webshop-opening en in de footer (docs/VORMGEVING.md › Bewust dubbel).
      sub: plural(routines.length, 'protocol', 'protocollen')
    }} actions={/* Hoofdknop: naar de protocollen zelf. Wie nog twijfelt, doet eerst de huidtest. */
    <>
            <a href={complete ? '#anchor-routine' : '#concern'} className={btnPrimaryLg} data-id="element-8583" data-mp="31194s1r5hzh">
              {pageActions.startProtocol}
            </a>
            {copy.hero.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-8584" data-mp="vo3tmg9lfs6q">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-8580" data-mp="29va4v5uyvcdu" />

      {complete && <RoutineAnchor routine={complete} data-id="element-8585" data-mp="lxsk8e754qsg" />}

      {/* Of begin bij je klacht: de klachtkiezer van Home, rustig. De huidtest staat al in de opening. */}
      <ConcernFinder copy={{
      ...copy.concerns,
      quizLine: ''
    }} still data-id="element-8586" data-mp="1enhqb5ypjh0x">
        {routines.length > 1 && <details className="group mt-14 border-t border-ink/15 pt-6" data-id="element-8587" data-mp="z5hv5e8y8z2p">
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-4 text-ink [&::-webkit-details-marker]:hidden" data-id="element-8588" data-mp="z7kreg0p3ofv">
              <span className="type-title" data-id="element-8589" data-mp="p8dflx9m01uv">{fill(copy.compareHeading)}</span>
              <ChevronDownIcon className="h-5 w-5 shrink-0 transition-transform duration-200 ease-soft group-open:rotate-180" strokeWidth={1.5} aria-hidden="true" data-id="element-8590" data-mp="u659gwttdy1p" />
            </summary>
            <RoutineCompare heading={fill(copy.compareHeading)} hideHeading data-id="element-8591" data-mp="1mv2lwjbch0mj" />
          </details>}

        {copy.smallStart.body && <div className="mt-10 flex max-w-3xl flex-wrap items-baseline gap-x-6 gap-y-3" data-id="element-8592" data-mp="185elrlewg80e">
            <p className="text-[17px] leading-relaxed text-muted" data-id="element-8593" data-mp="1d47p4l0h2imu">{fill(copy.smallStart.body)}</p>
            {copy.smallStart.shopCta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-8594" data-mp="20sqxt1nfoxfg">
                {fill(copy.smallStart.shopCta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8595" data-mp="3qbz7bu43entq" />
              </Link>}
          </div>}
      </ConcernFinder>

      {/* Wat er na je bestelling gebeurt: de tijdlijn van Home, stil. Eigen kop, dezelfde momenten. */}
      <HomeProtocols copy={{
      ...copy.buildUp,
      eyebrow: '',
      cta: ''
    }} still titleId="routines-build-up" data-id="element-8596" data-mp="3yh5657ve16k" />

      {/* Abonnement en vragen: dezelfde blokken als op de webshop, hier in de korte versie. */}
      <SubscriptionBlock size="short" data-id="element-8597" data-mp="37f7jr8hipmjv" />

      <ShopFaq limit={ROUTINES_FAQ_COUNT} data-id="element-8598" data-mp="32etj4bi8xevv" />

      {/* Het slot van de funnel: wie nog twijfelt doet de huidtest (WhatsApp staat al onder de koopknop);
          salonklanten lezen hier hun code. Op blush: de footer is al klei. */}
      <ClosingBand tone="blush" titleId="salon-client" title={<HeadingText heading={copy.salonClient.heading} data-id="element-8600" data-mp="vmizjjuh8v32" />} body={copy.salonClient.body ? fill(copy.salonClient.body, {
      code: SALON_CODE
    }) : undefined} actions={copy.salonClient.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8601" data-mp="3kfxldspxq9rg">
              {fill(copy.salonClient.cta)}
            </button>} data-id="element-8599" data-mp="3camtbo8hswah" />
    </>;
}